import type { Position } from "../types/grid.js";
import { resolveLootTable } from "./lootTable.js";
import type { LootTable, LootTableLookup } from "./lootTable.js";

/**
 * I15a — pure container-open resolver.
 *
 * Given an interact request (container metadata + player cell) decides
 * what happens: locked / out-of-range / already-opened / open-and-roll.
 * Returns a discriminated result the caller applies to WorldStore and
 * the feed, so the logic is unit-testable without a running dispatcher.
 */

export interface ContainerOpenRequest {
  readonly containerId: string;
  readonly cell: Position;
  readonly locked: boolean;
  readonly alreadyOpened: boolean;
  readonly lootTableId?: string;
}

export interface ContainerOpenDeps {
  readonly playerCell: Position;
  readonly lookupLootTable: LootTableLookup;
}

export type ContainerOpenResult =
  | { readonly kind: "out_of_range" }
  | { readonly kind: "locked" }
  | { readonly kind: "already_opened" }
  | {
      readonly kind: "opened";
      readonly drops: readonly string[];
      readonly cell: Position;
    };

/** Manhattan distance ≤ 1 (same cell counts so the player can open through a doorway). */
function adjacent(a: Position, b: Position): boolean {
  return Math.abs(a.x - b.x) + Math.abs(a.z - b.z) <= 1;
}

export function openContainer(
  req: ContainerOpenRequest,
  deps: ContainerOpenDeps,
): ContainerOpenResult {
  if (!adjacent(req.cell, deps.playerCell)) return { kind: "out_of_range" };
  if (req.locked) return { kind: "locked" };
  if (req.alreadyOpened) return { kind: "already_opened" };

  const drops: string[] = [];
  if (req.lootTableId !== undefined) {
    const table: LootTable | undefined = deps.lookupLootTable(req.lootTableId);
    if (table) {
      const resolved = resolveLootTable(table, deps.lookupLootTable);
      for (const d of resolved) {
        for (let i = 0; i < d.quantity; i++) drops.push(d.itemId);
      }
    }
  }
  return { kind: "opened", drops, cell: req.cell };
}
